import type { MultipleQueriesResponse } from '@algolia/client-search'
import dayjs from 'dayjs'
import { HITS_PER_SHOWCASE } from './algolia/constants'
import type { AlgoliaEventItem } from '~/composables/types/algolia'

import { buildAlgoliaAnalyticsTags } from '~/utils/algoliaHelpers'

export const useHomepageAlgolia = async () => {
  const { locale } = useI18n()
  const {
    public: { appEnv },
  } = useRuntimeConfig()

  // Use useState to create a shared state that persists across composable re-executions
  // This prevents algoliaResults from becoming undefined when the composable is called again
  const algoliaResults = useState<
    MultipleQueriesResponse<AlgoliaEventItem> | undefined
  >('homepage-algolia-results', () => undefined)

  const todayInSecs = dayjs().unix()
  const sevenDaysLaterInSecs = dayjs()
    .add(7, 'day')
    .set('hour', 23)
    .set('minute', 59)
    .set('second', 0)
    .set('millisecond', 0)
    .unix()

  const homePageQueryParams = [
    {
      indexName: getTopEventListIndexName(locale.value),
      hitsPerPage: HITS_PER_SHOWCASE,
      filters: `dates_ts:${todayInSecs} TO ${sevenDaysLaterInSecs} OR dates_private_ts:${todayInSecs} TO ${sevenDaysLaterInSecs}`,
      isBoxes: false,
    },
    {
      indexName: getEventListSortByDateDescIndexName(locale.value),
      hitsPerPage: HITS_PER_SHOWCASE,
      isBoxes: false,
    },
    {
      indexName: getTopGiftIdeasIndexName(locale.value),
      hitsPerPage: HITS_PER_SHOWCASE * 2,
      isBoxes: false,
    },
    {
      indexName: getEventListIndexName(locale.value),
      filters: '(has_promotions:true)',
      hitsPerPage: HITS_PER_SHOWCASE,
      isBoxes: false,
    },
    {
      indexName: getBoxesIndexName(locale.value),
      hitsPerPage: HITS_PER_SHOWCASE,
      isBoxes: true,
    },
  ]

  const homePageQueries = homePageQueryParams.map((params) => {
    let filters = params.isBoxes ? '' : 'has_events:true'
    delete params.isBoxes

    if (params.filters) {
      filters += ` AND (${params.filters})`
    }

    return {
      ...params,
      clickAnalytics: true,
      analyticsTags: buildAlgoliaAnalyticsTags(appEnv),
      filters,
      aroundLatLngViaIP: import.meta.client,
      aroundRadius: 'all',
      aroundPrecision: [
        { from: 0, value: 15000 },
        { from: 30000, value: 30000 },
        { from: 150000, value: 100000 },
      ],
    }
  })

  const {
    data: algoliaResultsRaw,
    pending,
    status,
  } = await useLazyAsyncData<MultipleQueriesResponse<AlgoliaEventItem>>(
    `algoliaResults-homepage-${import.meta.client}`,
    () => {
      return useAlgoliaSearchClient().search(homePageQueries)
    }
  )

  // Initialize algoliaResults from useState if not already set, or use existing value
  // This ensures we preserve the value across composable re-executions
  if (!algoliaResults.value && algoliaResultsRaw.value) {
    algoliaResults.value = algoliaResultsRaw.value
  }

  // Watch algoliaResultsRaw and update algoliaResults, but never set it to undefined
  // Since algoliaResults is now from useState, it persists across composable re-executions
  watch(
    algoliaResultsRaw,
    (newVal) => {
      // Only update if we have new data - preserve previous value if newVal is undefined
      if (newVal) {
        algoliaResults.value = newVal
      }
      // If newVal is undefined, algoliaResults.value keeps its previous value (from useState)
    },
    { immediate: true }
  )

  return {
    algoliaResults,
    pending,
    status,
  }
}
